import React from "react";
import {
	Button,
	ButtonProps,
	ChildrenProps,
	ClassNameProps,
	Grid,
	Heading,
	Modal,
	ModalProps,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import { Icon } from "../../../../Components/Icon";
import { permissions } from "../../../../Utilities";
import {
	AnnouncementCreateModalAnimatedIconController,
	AnnouncementCreatePermissionRequired,
} from "../../Announcement";
import {
	CalendarEventCreateModalAnimatedIconController,
	CalendarEventCreatePermissionRequired,
} from "../../CalendarEvent";
import { NewsletterCreateModalAnimatedIconController, NewsletterCreatePermissionRequired } from "../../Newsletter";
import { PostCreateModalAnimatedIconController, PostCreatePermissionRequired } from "../../Post";

const createPermissions = [
	permissions.Announcement.Create.Admin,
	permissions.Announcement.Create.Basic,
	permissions.Newsletter.Create.Admin,
	permissions.Newsletter.Create.Basic,
	permissions.Post.Create.Admin,
	permissions.Post.Create.Basic,
	permissions.CalendarEvent.Create.Admin,
	permissions.CalendarEvent.Create.Basic,
];

export function CommunicationCreatePermissionRequired({ children }: Partial<ChildrenProps>) {
	return (
		<PermissionRequired requiredPermissions={createPermissions} checkMethod="or">
			{children}
		</PermissionRequired>
	);
}

export type CommunicationCreateModalControllerProps = Pick<ButtonProps, "variant" | "size" | "title"> &
	Omit<CommunicationCreateModalProps, "ifRef" | "onClose" | "className" | "style"> &
	Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function CommunicationCreateModalController({
	variant,
	size,
	title,
	className,
	style,
	children = (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
			<Icon.Plus size={16} /> Create
		</Row>
	),
	...props
}: CommunicationCreateModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant={variant} size={size} onClick={openModal} title={title} className={className} style={style}>
				{children}
			</Button>

			<Modal.If condition={isModalOpen}>
				<CommunicationCreateModal onClose={closeModal} {...props} />
			</Modal.If>
		</React.Fragment>
	);
}

const gridResponsiveTracks = {
	0: 1,
	400: 2,
};

export type CommunicationCreateModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function CommunicationCreateModal({ ifRef, onClose, className, style }: CommunicationCreateModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={className} style={{ width: "50rem", ...style }}>
			<Modal.Header>
				<Heading.H2 noMargin>Create</Heading.H2>
			</Modal.Header>
			<Modal.Body style={{ paddingBottom: "4rem" }}>
				<Grid responsiveTracks={gridResponsiveTracks} style={{ alignItems: "center" }} gap="1rem">
					<AnnouncementCreatePermissionRequired>
						<AnnouncementCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
					</AnnouncementCreatePermissionRequired>
					<NewsletterCreatePermissionRequired>
						<NewsletterCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
					</NewsletterCreatePermissionRequired>
					<PostCreatePermissionRequired>
						<PostCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
					</PostCreatePermissionRequired>
					<CalendarEventCreatePermissionRequired>
						<CalendarEventCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
					</CalendarEventCreatePermissionRequired>
				</Grid>
			</Modal.Body>
		</Modal>
	);
}
